import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, CardHeader, IconButton, Tooltip, Button,
  Typography, CircularProgress, Alert, Chip, Stack, useTheme,
} from '@mui/material';
import { Refresh, FilterAlt, EditOutlined } from '@mui/icons-material';
import Groups2Icon from '@mui/icons-material/Groups2';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InsightsIcon from '@mui/icons-material/Insights';
import ShareIcon from '@mui/icons-material/Share';
import DownloadIcon from '@mui/icons-material/Download';
import StorageIcon from '@mui/icons-material/Storage';
import {
  LineChart, Line,
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  RadialBarChart, RadialBar,
  Tooltip as RechartTooltip, Legend,
  XAxis, YAxis, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import apiClient from 'utils/ApiClient';
import { infralytiqsAnalyticsSitePathSegment } from 'utils/infralytiqsAnalyticsPath';
import { ReportDefinition, TimeRangeDef, FilterDef, DamFolderQuotaEntry } from '../Settings/reportDsl';
import { PaginatedReportTable } from './PaginatedReportTable';
import { ReportDetailCard } from './ReportDetailCard';
import { GeoHeatmapPanel } from './GeoHeatmapPanel';
import { ReportPanelApiAction } from './ReportPanelApiAction';
import { SelectablePieChart } from './SelectablePieChart';
import { ByteUnitSystem, formatDecimalMegabytes, formatStorageBytes } from './formatStorageBytes';
import {
  DamStorageQuotaBar,
  hasAssetCountMetric,
  readQuotaMetricFromRows,
  readSnapshotCountFromRows,
  StorageQuotaNoStats,
  withSnapshotCountMetric,
} from './DamStorageQuotaBar';
import { DamMultiFolderStorageQuotaBar } from './DamMultiFolderStorageQuotaBar';
import { reportsPanelToolbarButtonSx, reportsToolbarSurfaceBg } from './reportsToolbarSurface';
import {
  fillTimeSeriesBuckets,
  formatTimeBucketAxisUnitLabel,
  panelMinHeightPx,
  resolveEffectiveChartTimeRange,
} from './chartTimeRange';
import { REPORT_TABLE_EXPORT_MAX_ROWS } from './exportReportTableCsv';
import {
  buildUniqCounterExportQuery,
  downloadUniqCounterCsv,
} from './uniqCounterCsvExport';

interface ReportPanelProps {
  report: ReportDefinition;
  tenantId: string;
  /** May be empty when the ClickHouse IL config targets all sites for the tenant. */
  siteId?: string;
  /** Optional global override for time range (e.g. a suite-level picker). */
  timeRangeOverride?: TimeRangeDef;
  /** Optional runtime filters (e.g. from a drill-down selection). */
  runtimeFilters?: FilterDef[];
  /** Invoked when a row is clicked in a table visualization (drill-down). */
  onRowClick?: (row: Record<string, any>, report: ReportDefinition) => void;
  /** Invoked when a counter / number panel with onPanelClick is clicked. */
  onPanelClick?: (report: ReportDefinition) => void;
  /** When provided, renders an Edit button in the title bar that opens the
   *  report's configuration (e.g. deep-link to the suite settings editor). */
  onEdit?: (report: ReportDefinition) => void;
  /** Badge shown in the header (e.g. "Drill-down: user_id = 42"). */
  contextBadge?: string;
  /**
   * IANA timezone (e.g. "Europe/London") used for ClickHouse day/hour
   * bucketing on the server and for label formatting in the chart axes.
   * Comes from the report suite's `timezone` field. When omitted, both
   * the server and client default to UTC.
   */
  timezone?: string;
  /** When true, table panels omit their card header (parent supplies context). */
  hidePanelHeader?: boolean;
  /** When false, hide the panel ingest-API (API Specs) button. Defaults to true. */
  showApiAction?: boolean;
}

// Palette tuned to avoid the blue / indigo / purple band so chart series
// stay visually distinct from the app's primary/secondary brand colors
// (which already lean blue-ish). The order is chosen so the FIRST color
// — used for single-series charts (area, pie/donut highlight, gauge) —
// is a strong, friendly emerald that reads well on both light and dark
// themes.
const CHART_COLORS = [
  '#10b981', // emerald
  '#f97316', // orange
  '#ec4899', // pink
  '#14b8a6', // teal
  '#f59e0b', // amber
  '#ef4444', // red
  '#84cc16', // lime
  '#eab308', // yellow
];

/**
 * Display-only overrides for low-cardinality categorical dimensions.
 *
 * The SDK / UA parser stores values lowercased (`desktop`, `ios`, `macos`),
 * which is great for SQL grouping but ugly in chart legends and tooltips.
 * We map raw → display so pies, bars, and tooltips show "Desktop" / "iOS"
 * / "macOS" while the underlying row data, drill-down `passField` values,
 * and table cells remain untouched (so filters, deep-links and ClickHouse
 * round-trips keep using the canonical lowercase form).
 *
 * Matching is case-insensitive on a trimmed value. Anything not listed
 * here falls through to a generic title-case for the four "known string"
 * dimensions (`device_type`, `device_os`, `event_type`, `event_subtype`).
 */
const DIMENSION_LABEL_OVERRIDES: Record<string, Record<string, string>> = {
  device_type: {
    desktop: 'Desktop',
    mobile: 'Mobile',
    tablet: 'Tablet',
    bot: 'Bot',
    smarttv: 'Smart TV',
    'smart-tv': 'Smart TV',
    wearable: 'Wearable',
    console: 'Console',
    embedded: 'Embedded',
  },
  device_os: {
    ios: 'iOS',
    macos: 'macOS',
    'mac os': 'macOS',
    'mac os x': 'macOS',
    windows: 'Windows',
    'windows phone': 'Windows Phone',
    android: 'Android',
    linux: 'Linux',
    ubuntu: 'Ubuntu',
    fedora: 'Fedora',
    chromeos: 'ChromeOS',
    'chrome os': 'ChromeOS',
    freebsd: 'FreeBSD',
    openbsd: 'OpenBSD',
  },
  event_type: {
    lucidlink_asset_migration_transfer_completed: 'Migrated',
    lucidlink_asset_migration_skipped: 'Skipped',
    lucidlink_asset_migration_metadata_completed: 'Migrated',
    lucidlink_asset_migration_metadata_failed: 'Failed',
    lucidlink_asset_migration_checksum_verified: 'Passed',
    lucidlink_asset_migration_checksum_failed: 'Failed',
    lucidlink_asset_migration_checksum_mismatch: 'Failed',
    lucidlink_asset_migration_task_completed: 'Completed',
    lucidlink_asset_migration_task_failed: 'Failed',
  },
};

const PRETTY_STRING_DIMENSIONS = new Set([
  'device_type', 'device_os', 'event_type', 'event_subtype',
]);

const titleCase = (s: string): string =>
  s.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatBytesForChart = (n: number, system: ByteUnitSystem = 'binary'): string =>
  formatStorageBytes(n, { system });

/** Last path segment for DAM folder_path custom dimensions (pie / table labels). */
const formatDamFolderPathLabel = (raw: string): string => {
  const parts = raw.split('/').filter(Boolean);
  return parts[parts.length - 1] || raw;
};

const lookupCustomDimensionLabel = (
  labels: Record<string, string> | undefined,
  raw: string,
): string | undefined => {
  if (!labels) return undefined;
  if (labels[raw]) return labels[raw];
  const key = raw.toLowerCase().trim();
  const hit = Object.entries(labels).find(([k]) => k.toLowerCase().trim() === key);
  const value = hit?.[1];
  return value && value.trim() !== '' ? value : undefined;
};

const formatDimensionValue = (
  field: string,
  raw: unknown,
  customLabels?: Record<string, string>,
): unknown => {
  if (raw === null || raw === undefined) return raw;
  const str = String(raw);
  if (!str) return raw;
  const custom = lookupCustomDimensionLabel(customLabels, str);
  if (custom) return custom;
  if (field === 'custom_dimensions.folder_path') {
    return formatDamFolderPathLabel(str);
  }
  const overrides = DIMENSION_LABEL_OVERRIDES[field];
  if (overrides) {
    const key = str.toLowerCase().trim();
    if (overrides[key]) return overrides[key];
  }
  if (PRETTY_STRING_DIMENSIONS.has(field)) {
    return titleCase(str.toLowerCase());
  }
  return raw;
};

/** Fixed height for dashboard counter hero blocks (icon + value + label). */
const COUNTER_STAT_BLOCK_HEIGHT_PX = 160;
/** Gap between counter stat block and description (px). */
const COUNTER_DESCRIPTION_GAP_PX = 8;

export const ReportPanel: React.FC<ReportPanelProps> = ({
  report, tenantId, siteId, timeRangeOverride, runtimeFilters, onRowClick, onPanelClick, onEdit, contextBadge, timezone, hidePanelHeader, showApiAction = true,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const pathSite = useMemo(() => infralytiqsAnalyticsSitePathSegment(siteId), [siteId]);

  // Recharts ships fixed inline-styled defaults for the hover tooltip
  // (near-white background, black text, white cursor strip), which clash
  // hard with the dark theme. We derive theme-aware props from the MUI
  // palette and reuse them across every chart variant in this panel
  // (line, area, bar, pie / donut) so the tooltip chrome matches the
  // rest of the app — and stays correct when the user toggles theme.
  const tooltipContentStyle: React.CSSProperties = {
    backgroundColor: isDark ? theme.palette.background.paper : '#ffffff',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: theme.shadows[6],
    color: theme.palette.text.primary,
    padding: '6px 10px',
  };
  const tooltipLabelStyle: React.CSSProperties = {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    marginBottom: 2,
  };
  const tooltipItemStyle: React.CSSProperties = {
    color: theme.palette.text.primary,
  };
  const tooltipCursor = {
    stroke: theme.palette.divider,
    strokeWidth: 1,
    fill: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
  };
  const tooltipWrapperStyle: React.CSSProperties = { outline: 'none' };

  const vis = report.visualizations?.[0] || { type: 'table' };
  const isCounter = vis.type === 'counter';
  const isStorageQuotaBar = vis.type === 'storage_quota_bar';
  const isStorageQuotaStackedBar = vis.type === 'storage_quota_stacked_bar';
  const isTimeSeriesChart = vis.type === 'line' || vis.type === 'area' || vis.type === 'bar';
  const isPieChart = vis.type === 'pie' || vis.type === 'donut';
  const usesTitledPanelShell = (
    isCounter || isStorageQuotaBar || isStorageQuotaStackedBar || isTimeSeriesChart || isPieChart
  ) && !hidePanelHeader;
  const usesFlexibleChartHeight = usesTitledPanelShell && isTimeSeriesChart;
  const panelMinHeight = panelMinHeightPx(report.layout?.h);
  const panelTitle = vis.title || report.name;
  const panelClickTarget = report.interactions?.onPanelClick;
  const isPanelClickable = Boolean(
    onPanelClick && panelClickTarget && (panelClickTarget.targetReportName || panelClickTarget.targetReportId),
  );
  const valueFormat = vis.options?.valueFormat as string | undefined;
  const byteUnitSystem = (vis.options?.byteUnitSystem as ByteUnitSystem | undefined) ?? 'binary';
  const formatMetricValue = (v: unknown): string | number => {
    const n = typeof v === 'number' ? v : Number(v) || 0;
    if (valueFormat === 'decimal_mb') return formatDecimalMegabytes(n);
    if (valueFormat === 'bytes') return formatBytesForChart(n, byteUnitSystem);
    return typeof v === 'number' ? v : n;
  };
  const metricLabels = (report.query.metrics || []).map((m, i) => m.label || `metric_${i}`);
  const dimensionLabels = (report.query.dimensions || []).map((d, i) => d.label || `dim_${i}`);

  const isTable = vis.type === 'table' || (!vis.type);
  const isGeoHeat = vis.type === 'geoheat';
  const isDetailCard = vis.type === 'detail_card';

  const storageQuotaQuery = useMemo(() => {
    if ((!isStorageQuotaBar && !isStorageQuotaStackedBar) || !report.query) return null;
    const baseQuery = {
      ...report.query,
      metrics: withSnapshotCountMetric(report.query.metrics || []),
      timeRange: report.query.timeRange
        ? { ...report.query.timeRange, granularity: undefined }
        : report.query.timeRange,
    };
    if (isStorageQuotaStackedBar) {
      return {
        ...baseQuery,
        dimensions: baseQuery.dimensions?.length
          ? baseQuery.dimensions
          : [{ field: 'custom_dimensions.folder_path', label: 'DAM folder' }],
      };
    }
    // Keep the report's own grouping. Do not inject `site_id` here: tenant
    // quota bars on an all-sites ClickHouse config would start summing every
    // environment against a single-folder cap. Program rollups that need
    // per-site argMax declare `site_id` (and folder) on the saved query.
    return baseQuery;
  }, [isStorageQuotaBar, isStorageQuotaStackedBar, report.query]);

  const effectiveChartTimeRange = useMemo(
    () => (isTimeSeriesChart
      ? resolveEffectiveChartTimeRange(report.query.timeRange, timeRangeOverride, true)
      : undefined),
    [isTimeSeriesChart, report.query.timeRange, timeRangeOverride],
  );

  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [, setColumns] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [folderStorageDownload, setFolderStorageDownload] = useState<(() => void) | null>(null);
  const [csvDownloading, setCsvDownloading] = useState(false);
  const [csvDownloadError, setCsvDownloadError] = useState<string | null>(null);

  const uniqCounterExportQuery = useMemo(
    () => (isCounter ? buildUniqCounterExportQuery(report.query) : null),
    [isCounter, report.query],
  );

  /** Store a function in state — must wrap; passing a function directly to setState invokes it. */
  const registerFolderStorageDownload = useCallback((handler: (() => void) | null) => {
    setFolderStorageDownload(() => handler);
  }, []);

  const fetchData = useCallback(async () => {
    // Table and geoheat visualizations delegate to dedicated components
    // (<PaginatedReportTable /> / <GeoHeatmapPanel />) which own their
    // own fetch lifecycle. Skip this chart-path fetch to avoid a
    // duplicate ClickHouse round-trip.
    if (isTable || isGeoHeat) return;
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, any> = {
        query: (isStorageQuotaBar || isStorageQuotaStackedBar) && storageQuotaQuery
          ? storageQuotaQuery
          : report.query,
        runtimeFilters: runtimeFilters || [],
        timeRangeOverride: isTimeSeriesChart && effectiveChartTimeRange
          ? effectiveChartTimeRange
          : timeRangeOverride,
      };
      if (timezone) body.timezone = timezone;
      const res: any = await apiClient.post(`/il/analytics/${tenantId}/${pathSite}/report-query`, body);
      setRows(res?.data ?? []);
      setColumns(res?.columns ?? []);
    } catch (err: any) {
      setError(err?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [isTable, isGeoHeat, isStorageQuotaBar, isStorageQuotaStackedBar, isTimeSeriesChart, effectiveChartTimeRange, storageQuotaQuery, report.query, tenantId, pathSite, timeRangeOverride, runtimeFilters, timezone]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!isStorageQuotaStackedBar) setFolderStorageDownload(null);
  }, [isStorageQuotaStackedBar]);

  const handleUniqCounterCsvDownload = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!tenantId || !uniqCounterExportQuery) return;
    setCsvDownloading(true);
    setCsvDownloadError(null);
    try {
      const result = await downloadUniqCounterCsv({
        tenantId,
        pathSite,
        exportQuery: uniqCounterExportQuery,
        runtimeFilters: runtimeFilters || [],
        timeRangeOverride,
        timezone,
        filenameTitle: panelTitle,
      });
      if (result.empty) {
        setCsvDownloadError('No rows to export for the current filters.');
        return;
      }
      if (result.truncated) {
        const totalLabel = (result.totalHint ?? result.exported).toLocaleString();
        setCsvDownloadError(
          `Exported first ${REPORT_TABLE_EXPORT_MAX_ROWS.toLocaleString()} of ${totalLabel} rows (server limit).`,
        );
      }
    } catch (err: unknown) {
      setCsvDownloadError(err instanceof Error ? err.message : 'CSV export failed');
    } finally {
      setCsvDownloading(false);
    }
  }, [
    tenantId,
    uniqCounterExportQuery,
    pathSite,
    runtimeFilters,
    timeRangeOverride,
    timezone,
    panelTitle,
  ]);

  const dimensionValueLabels = vis.options?.dimensionValueLabels as Record<string, string> | undefined;

  // Map raw rows -> chart-friendly records using labels.
  const chartData = useMemo(() => {
    const mapped = rows.map((r) => {
      const out: Record<string, any> = {};
      // bucket may exist when granularity is set
      if (r.bucket !== undefined) out.bucket = r.bucket;
      (report.query.dimensions || []).forEach((d, i) => {
        out[d.label || `dim_${i}`] = formatDimensionValue(
          d.field,
          r[`dim_${i}`],
          dimensionValueLabels,
        );
      });
      (report.query.metrics || []).forEach((m, i) => {
        const val = r[`metric_${i}`];
        out[m.label || `metric_${i}`] = typeof val === 'string' ? Number(val) || 0 : (val ?? 0);
      });
      return out;
    });

    if (!isTimeSeriesChart || !effectiveChartTimeRange?.granularity) return mapped;

    return fillTimeSeriesBuckets(
      mapped,
      'bucket',
      metricLabels,
      effectiveChartTimeRange,
      effectiveChartTimeRange.granularity,
    );
  }, [rows, report.query.dimensions, report.query.metrics, isTimeSeriesChart, effectiveChartTimeRange, metricLabels, dimensionValueLabels]);

  const xKey = chartData[0]?.bucket !== undefined ? 'bucket' : (dimensionLabels[0] || 'metric_0');

  /**
   * Format ClickHouse bucket values for the chart axis / tooltip.
   *
   * ClickHouse JSON serializes DateTime as `"2026-04-24 00:00:00"` (no `T`,
   * no timezone). The bucket's wall-clock is rendered in whatever timezone
   * the server used for `toStartOf*(event_time, <tz>)`; we mirror that
   * timezone here so the label corresponds to the suite's calendar day,
   * not the browser's local one. We coerce the value to ISO-8601 + `Z`
   * (which Safari's parser accepts) — the absolute instant is wrong by
   * a fixed offset, but `Intl.DateTimeFormat({ timeZone })` re-shifts it
   * back to the right wall-clock for display.
   *
   * Granularity drives the label density:
   *   - day   / week  → "Apr 24"
   *   - hour  / minute→ "Apr 24 14:00"
   *   - month         → "Apr 2026"
   * If the value isn't parseable we fall back to the raw string so the
   * panel never silently swallows weird bucket values.
   */
  const formatBucket = useCallback((raw: unknown): string => {
    if (raw === null || raw === undefined) return '';
    const str = String(raw);
    const isoish = /^\d{4}-\d{2}-\d{2} /.test(str) ? `${str.replace(' ', 'T')}Z` : str;
    const d = new Date(isoish);
    if (Number.isNaN(d.getTime())) return str;
    const granularity = effectiveChartTimeRange?.granularity
      ?? timeRangeOverride?.granularity
      ?? report.query.timeRange?.granularity
      ?? 'day';
    const tz = timezone || 'UTC';
    switch (granularity) {
      case 'minute':
      case 'hour':
        return d.toLocaleString(undefined, {
          month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
          timeZone: tz,
        });
      case 'month':
        return d.toLocaleString(undefined, { month: 'short', year: 'numeric', timeZone: tz });
      case 'week':
      case 'day':
      default:
        return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', timeZone: tz });
    }
  }, [effectiveChartTimeRange?.granularity, timeRangeOverride?.granularity, report.query.timeRange?.granularity, timezone]);

  const isTimeBucket = xKey === 'bucket';
  const chartGranularity = effectiveChartTimeRange?.granularity
    ?? timeRangeOverride?.granularity
    ?? report.query.timeRange?.granularity;

  const renderSeriesXAxis = () => {
    if (isTimeBucket) {
      return (
        <XAxis
          dataKey={xKey}
          tick={false}
          tickLine={false}
          axisLine={{ stroke: theme.palette.divider }}
          label={{
            value: formatTimeBucketAxisUnitLabel(chartGranularity),
            position: 'insideBottomRight',
            offset: -4,
            style: {
              fontSize: 12,
              fill: theme.palette.text.secondary,
              fontWeight: 500,
            },
          }}
        />
      );
    }
    return (
      <XAxis
        dataKey={xKey}
        tick={{ fontSize: 11 }}
        interval="preserveStartEnd"
        minTickGap={5}
      />
    );
  };

  const renderChartContainer = (chart: React.ReactElement) => {
    if (usesFlexibleChartHeight) {
      return (
        <Box sx={{ flex: 1, minHeight: 160, minWidth: 0, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            {chart}
          </ResponsiveContainer>
        </Box>
      );
    }
    return (
      <ResponsiveContainer width="100%" height={280}>
        {chart}
      </ResponsiveContainer>
    );
  };

  // Table visualizations are rendered by <PaginatedReportTable /> with their
  // own server-side paginated / sortable / filterable fetch lifecycle.
  if (isTable) {
    return (
      <PaginatedReportTable
        report={report}
        tenantId={tenantId}
        siteId={siteId}
        timeRangeOverride={timeRangeOverride}
        runtimeFilters={runtimeFilters}
        onRowClick={onRowClick}
        contextBadge={contextBadge}
        timezone={timezone}
        hideHeader={hidePanelHeader}
        onEdit={onEdit}
        showApiAction={showApiAction}
      />
    );
  }

  // Geographic heatmap delegates to its own component — it loads the
  // Google Maps JS API on-demand using the per-domain browser key from
  // /system/client/config.
  if (isGeoHeat) {
    return (
      <GeoHeatmapPanel
        report={report}
        tenantId={tenantId}
        siteId={siteId}
        timeRangeOverride={timeRangeOverride}
        runtimeFilters={runtimeFilters}
        contextBadge={contextBadge}
        timezone={timezone}
        onEdit={onEdit}
        showApiAction={showApiAction}
      />
    );
  }

  // Read-only single-event "detail card" — renders the one matched row
  // (typically narrowed by a runtime `event_id` filter from a row click)
  // as a list of label : value pairs.
  if (isDetailCard) {
    return (
      <Box sx={{ p: hidePanelHeader ? 0 : 1 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <ReportDetailCard report={report} row={rows[0]} />
        )}
      </Box>
    );
  }

  const yAxisFormatProps = (valueFormat === 'bytes' || valueFormat === 'decimal_mb')
    ? { tickFormatter: (v: number) => String(formatMetricValue(v)) }
    : {};

  const renderChart = () => {
    if (loading) {
      return (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          ...(usesFlexibleChartHeight ? { flex: 1, minHeight: 160 } : { py: 6 }),
        }}
        >
          <CircularProgress />
        </Box>
      );
    }
    if (error) return <Alert severity="error">{error}</Alert>;
    if (chartData.length === 0) {
      if (isStorageQuotaBar || isStorageQuotaStackedBar || isPieChart) return <StorageQuotaNoStats />;
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No data for the selected range.
        </Typography>
      );
    }

    switch (vis.type) {
      case 'line':
        return renderChartContainer(
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            {renderSeriesXAxis()}
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} {...yAxisFormatProps} />
            <RechartTooltip
              labelFormatter={isTimeBucket ? formatBucket : undefined}
              formatter={(v: unknown, name: string) => [formatMetricValue(v), name]}
              contentStyle={tooltipContentStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={tooltipItemStyle}
              cursor={tooltipCursor}
              wrapperStyle={tooltipWrapperStyle}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {metricLabels.map((label, i) => (
              <Line key={label} type="monotone" dataKey={label} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>,
        );

      case 'area':
        return renderChartContainer(
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            {renderSeriesXAxis()}
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} {...yAxisFormatProps} />
            <RechartTooltip
              labelFormatter={isTimeBucket ? formatBucket : undefined}
              formatter={(v: unknown, name: string) => [formatMetricValue(v), name]}
              contentStyle={tooltipContentStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={tooltipItemStyle}
              cursor={tooltipCursor}
              wrapperStyle={tooltipWrapperStyle}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {metricLabels.map((label, i) => (
              <Area key={label} type="monotone" dataKey={label}
                stroke={CHART_COLORS[i % CHART_COLORS.length]}
                fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.25} />
            ))}
          </AreaChart>,
        );

      case 'bar':
        return renderChartContainer(
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            {renderSeriesXAxis()}
            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} {...yAxisFormatProps} />
            <RechartTooltip
              labelFormatter={isTimeBucket ? formatBucket : undefined}
              formatter={(v: unknown, name: string) => [formatMetricValue(v), name]}
              contentStyle={tooltipContentStyle}
              labelStyle={tooltipLabelStyle}
              itemStyle={tooltipItemStyle}
              cursor={{ fill: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' }}
              wrapperStyle={tooltipWrapperStyle}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {metricLabels.map((label, i) => (
              <Bar key={label} dataKey={label} fill={CHART_COLORS[i % CHART_COLORS.length]} />
            ))}
          </BarChart>,
        );

      case 'pie':
      case 'donut': {
        const mLabel = metricLabels[0];
        if (vis.options?.interactiveLegend) {
          return (
            <SelectablePieChart
              chartData={chartData}
              nameKey={xKey}
              metricKey={mLabel}
              visType={vis.type}
              defaultView={
                vis.options?.defaultChartView === 'pie'
                || vis.options?.defaultChartView === 'donut'
                || vis.options?.defaultChartView === 'bar'
                  ? vis.options.defaultChartView
                  : undefined
              }
              valueFormat={valueFormat}
              byteDisplayUnit={vis.options?.byteDisplayUnit === 'GB' ? 'GB' : 'auto'}
              byteUnitSystem={byteUnitSystem}
            />
          );
        }
        const pieTotal = chartData.reduce(
          (sum, row) => sum + (Number(row[mLabel]) || 0),
          0,
        );
        const innerRadius = vis.type === 'donut' ? 60 : 0;
        return (
          <>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey={mLabel}
                  nameKey={xKey}
                  innerRadius={innerRadius}
                  outerRadius={90}
                  paddingAngle={vis.type === 'donut' ? 2 : 0}
                  label={false}
                  labelLine={false}
                >
                  {chartData.map((_entry, idx) => (
                    <Cell key={`c-${idx}`} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <RechartTooltip
                  formatter={(v: unknown, name: string) => [
                    formatMetricValue(v),
                    name,
                  ]}
                  contentStyle={tooltipContentStyle}
                  labelStyle={tooltipLabelStyle}
                  itemStyle={tooltipItemStyle}
                  wrapperStyle={tooltipWrapperStyle}
                />
              </PieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                mt: 0.5,
              }}
            >
              {chartData.map((row, idx) => {
                const name = String(row[xKey] ?? '');
                const value = Number(row[mLabel]) || 0;
                const pct = pieTotal > 0 ? (value / pieTotal) * 100 : 0;
                return (
                  <Box
                    key={`pie-legend-${idx}-${name}`}
                    sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, maxWidth: '100%' }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '2px',
                        flexShrink: 0,
                        bgcolor: CHART_COLORS[idx % CHART_COLORS.length],
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: 12,
                        lineHeight: 1.3,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }}
                    >
                      {name} {pct.toFixed(0)}%
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {pieTotal > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                Total: {(valueFormat === 'bytes' || valueFormat === 'decimal_mb') ? formatMetricValue(pieTotal) : pieTotal.toLocaleString()}
              </Typography>
            )}
          </>
        );
      }

      case 'gauge': {
        const mLabel = metricLabels[0];
        const value = Number(chartData[0]?.[mLabel] ?? 0);
        const gaugeData = [{ name: mLabel, value, fill: CHART_COLORS[0] }];
        return (
          <ResponsiveContainer width="100%" height={220}>
            <RadialBarChart innerRadius="60%" outerRadius="100%" data={gaugeData} startAngle={180} endAngle={0}>
              <RadialBar dataKey="value" cornerRadius={10} />
              <text x="50%" y="75%" textAnchor="middle" dominantBaseline="middle" style={{ fontSize: 24, fontWeight: 700 }}>
                {value.toLocaleString()}
              </text>
            </RadialBarChart>
          </ResponsiveContainer>
        );
      }

      case 'number': {
        const mLabel = metricLabels[0];
        const value = Number(chartData[0]?.[mLabel] ?? 0);
        return (
          <Box
            role={isPanelClickable ? 'button' : undefined}
            tabIndex={isPanelClickable ? 0 : undefined}
            onClick={isPanelClickable ? () => onPanelClick?.(report) : undefined}
            onKeyDown={isPanelClickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPanelClick?.(report);
              }
            } : undefined}
            sx={{
              textAlign: 'center',
              py: 4,
              ...(isPanelClickable && {
                cursor: 'pointer',
                borderRadius: 2,
                '&:hover': { bgcolor: 'action.hover' },
              }),
            }}
          >
            <Typography variant="h3" fontWeight={800}>{value.toLocaleString()}</Typography>
            <Typography variant="body2" color="text.secondary">{mLabel}</Typography>
          </Box>
        );
      }

      case 'storage_quota_stacked_bar': {
        const metrics = storageQuotaQuery?.metrics || report.query.metrics || [];
        const folderQuotas = (vis.options?.damFolderQuotas as DamFolderQuotaEntry[] | undefined) || [];
        if (!folderQuotas.length || rows.length === 0) {
          return <StorageQuotaNoStats />;
        }
        return (
          <DamMultiFolderStorageQuotaBar
            folderQuotas={folderQuotas}
            rows={rows}
            metrics={metrics}
            exportTitle={panelTitle}
            onRegisterDownload={registerFolderStorageDownload}
            showBarPercentage={Boolean((vis.options as { showBarPercentage?: boolean } | undefined)?.showBarPercentage)}
          />
        );
      }

      case 'storage_quota_bar': {
        const metrics = storageQuotaQuery?.metrics || report.query.metrics || [];
        if (readSnapshotCountFromRows(rows, metrics) === 0) {
          return <StorageQuotaNoStats />;
        }
        const usedMb = readQuotaMetricFromRows(rows, metrics, 'size_mb', 0);
        const maxStorageTb = Number(vis.options?.maxStorageTb) || 10;
        const damFolderPath = String(vis.options?.damFolderPath || '').trim();
        const assetCount = hasAssetCountMetric(metrics)
          ? readQuotaMetricFromRows(rows, metrics, 'asset_count', 1)
          : undefined;
        return (
          <DamStorageQuotaBar
            usedMb={usedMb}
            maxStorageTb={maxStorageTb}
            folderPath={damFolderPath || undefined}
            assetCount={assetCount}
          />
        );
      }

      // Counter is a "big single number" hero KPI panel. We render it as a
      // gradient tile (white text on a primary/secondary gradient) — matching
      // the visual language of the dataset tiles on /instant-updates/data-sets
      // — and pick a sensible icon based on the metric field so user / visitor
      // / session / pageview counters each get a meaningful glyph automatically.
      case 'counter': {
        const mLabel = metricLabels[0];
        const value = Number(chartData[0]?.[mLabel] ?? 0);
        const firstMetric = report.query.metrics?.[0];
        const field = (firstMetric?.field || '').toLowerCase();

        // Field → icon mapping. Falls back to a generic insights glyph so
        // the visual is never empty for non-user-style counters.
        let CounterIcon = InsightsIcon;
        if (field === 'user_id') CounterIcon = Groups2Icon;
        else if (field === 'anonymous_id') CounterIcon = PersonOutlineIcon;
        else if (field === 'session_id') CounterIcon = ScheduleIcon;
        else if (field === 'page_path') CounterIcon = VisibilityIcon;

        // Allow the report editor to override / disable the icon via
        // visualizations[0].options.icon: 'auto' | 'groups2' | 'person' | 'session'
        // | 'pageview' | 'insights' | 'none'.
        const iconOpt = String((vis.options as { icon?: string } | undefined)?.icon || 'auto');
        if (iconOpt !== 'auto' && iconOpt !== 'none') {
          if (iconOpt === 'groups2' || iconOpt === 'users') CounterIcon = Groups2Icon;
          else if (iconOpt === 'person' || iconOpt === 'visitor') CounterIcon = PersonOutlineIcon;
          else if (iconOpt === 'session' || iconOpt === 'schedule') CounterIcon = ScheduleIcon;
          else if (iconOpt === 'pageview' || iconOpt === 'visibility') CounterIcon = VisibilityIcon;
          else if (iconOpt === 'insights') CounterIcon = InsightsIcon;
          else if (iconOpt === 'share') CounterIcon = ShareIcon;
          else if (iconOpt === 'download') CounterIcon = DownloadIcon;
          else if (iconOpt === 'storage' || iconOpt === 'hard_disk' || iconOpt === 'disk') CounterIcon = StorageIcon;
        }
        const showIcon = iconOpt !== 'none';
        const dashPanel = theme.palette.dashboard;
        const useWelcomeCounterGradient = Boolean(
          dashPanel?.welcomePanelGradient
          && theme.palette.mode === 'light'
          && !dashPanel?.counterHeroGradient
          && !dashPanel?.reservedDatasetTileGradient,
        );
        // PROFESSIONAL light: match Consent_Records tile (grey → Adobe blue) on Infralytiqs counters.
        const counterHeroGradient = dashPanel?.reservedDatasetTileGradient
          ?? dashPanel?.counterHeroGradient
          ?? (useWelcomeCounterGradient
            ? dashPanel!.welcomePanelGradient!
            : `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`);
        // Expose the gradient's final (bottom-right) color stop as a CSS
        // variable so an embed can override ONLY that color (the rest of the
        // gradient is preserved). Falls back to the original stop when unset,
        // so the standalone app renders unchanged.
        const counterHeroGradientCss = counterHeroGradient.replace(
          /(rgba?\([^)]*\)|#[0-9a-fA-F]{3,8}|[a-zA-Z]+)(\s+100%\s*\)\s*)$/,
          (_m, color, tail) => `var(--il-counter-accent, ${color})${tail}`,
        );
        const heroTileFg = dashPanel?.heroTileForegroundColor;
        const counterIconColor = heroTileFg
          ?? (useWelcomeCounterGradient ? (dashPanel?.welcomePanelLeftTextColor ?? '#000000') : '#fff');
        const counterValueColor = heroTileFg
          ?? (useWelcomeCounterGradient ? (dashPanel?.welcomePanelLeftTextColor ?? '#000000') : '#fff');
        const counterLabelColor = dashPanel?.heroTileLabelColor
          ?? (useWelcomeCounterGradient ? 'rgb(20, 115, 230)' : 'rgba(255,255,255,0.85)');
        const useHeroTileLightSurface = Boolean(heroTileFg);

        return (
          <Box
            role={isPanelClickable ? 'button' : undefined}
            tabIndex={isPanelClickable ? 0 : undefined}
            onClick={isPanelClickable ? () => onPanelClick?.(report) : undefined}
            onKeyDown={isPanelClickable ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onPanelClick?.(report);
              }
            } : undefined}
            className="il-counter-tile"
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              background: counterHeroGradientCss,
              width: '100%',
              height: COUNTER_STAT_BLOCK_HEIGHT_PX,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              px: 2,
              boxShadow: (useWelcomeCounterGradient || useHeroTileLightSurface)
                ? '0 4px 14px rgba(0, 0, 0, 0.12)'
                : '0 6px 24px rgba(0,0,0,0.18) inset',
              ...(isPanelClickable && {
                cursor: 'pointer',
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: (useWelcomeCounterGradient || useHeroTileLightSurface)
                    ? '0 6px 18px rgba(0, 0, 0, 0.16)'
                    : '0 8px 28px rgba(0,0,0,0.28) inset',
                },
              }),
            }}
          >
            {showIcon && (
              <CounterIcon sx={{
                fontSize: 44,
                color: counterIconColor,
                opacity: (useWelcomeCounterGradient || useHeroTileLightSurface) ? 1 : 0.85,
              }} />
            )}
            <Typography
              sx={{
                fontSize: { xs: '2rem', sm: '2.25rem' },
                fontWeight: 800,
                color: counterValueColor,
                lineHeight: 1,
                textShadow: (useWelcomeCounterGradient || useHeroTileLightSurface) ? 'none' : '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              {value.toLocaleString()}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 700,
                color: counterLabelColor,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
              }}
            >
              {mLabel}
            </Typography>
          </Box>
        );
      }

      default:
        // `table` is handled by the early-return to <PaginatedReportTable />
        // above. Any other unknown type falls through to a simple message.
        return (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            Unsupported visualization type: {String(vis.type)}
          </Typography>
        );
    }
  };

  const panelClickHandlers = isPanelClickable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: () => onPanelClick?.(report),
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onPanelClick?.(report);
          }
        },
      }
    : {};

  const toolbarIconButtonSx = reportsPanelToolbarButtonSx(theme, {
    minWidth: 36,
    width: 36,
    height: 36,
    p: 0,
    borderRadius: 1,
  });

  const panelToolbarButtonColor = theme.palette.mode === 'light' ? undefined : 'secondary' as const;

  const refreshAction = (
    <Tooltip title="Refresh">
      <Button
        variant="outlined"
        color={panelToolbarButtonColor}
        size="small"
        onClick={fetchData}
        aria-label="Refresh report"
        sx={toolbarIconButtonSx}
      >
        <Refresh fontSize="small" />
      </Button>
    </Tooltip>
  );

  const apiAction = (
    <ReportPanelApiAction
      report={report}
      tenantId={tenantId}
      siteId={siteId}
      runtimeFilters={runtimeFilters}
      visible={showApiAction}
    />
  );

  const editAction = onEdit ? (
    <Tooltip title="Edit panel configuration">
      <Button
        variant="outlined"
        color={panelToolbarButtonColor}
        size="small"
        onClick={(e) => { e.stopPropagation(); onEdit(report); }}
        aria-label="Edit report panel"
        sx={toolbarIconButtonSx}
      >
        <EditOutlined fontSize="small" />
      </Button>
    </Tooltip>
  ) : null;

  const folderStorageDownloadAction = isStorageQuotaStackedBar && folderStorageDownload ? (
    <Tooltip title="Download table as CSV">
      <Button
        variant="outlined"
        color={panelToolbarButtonColor}
        size="small"
        onClick={() => folderStorageDownload?.()}
        aria-label="Download CSV"
        sx={toolbarIconButtonSx}
      >
        <DownloadIcon fontSize="small" />
      </Button>
    </Tooltip>
  ) : null;

  const uniqCounterDownloadAction = uniqCounterExportQuery ? (
    <Tooltip title={csvDownloading ? 'Preparing CSV…' : 'Download unique rows as CSV'}>
      <span>
        <Button
          variant="outlined"
          color={panelToolbarButtonColor}
          size="small"
          disabled={csvDownloading}
          onClick={(e) => { void handleUniqCounterCsvDownload(e); }}
          aria-label="Download CSV"
          sx={toolbarIconButtonSx}
        >
          {csvDownloading ? <CircularProgress size={18} /> : <DownloadIcon fontSize="small" />}
        </Button>
      </span>
    </Tooltip>
  ) : null;

  if (usesTitledPanelShell) {
    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          height: '100%',
          minHeight: panelMinHeight,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <Box
          className="il-panel-titlebar"
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
            bgcolor: (t) => reportsToolbarSurfaceBg(t),
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} component="div" sx={{ minWidth: 0 }}>
            {panelTitle}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
            {contextBadge && (
              <Chip size="small" icon={<FilterAlt fontSize="small" />} label={contextBadge} color="secondary" />
            )}
            {folderStorageDownloadAction}
            {uniqCounterDownloadAction}
            {apiAction}
            {editAction}
            {refreshAction}
          </Stack>
        </Box>

        {csvDownloadError && (
          <Alert
            severity={csvDownloadError.includes('server limit') ? 'warning' : 'error'}
            sx={{ mx: 2, mt: 1, flexShrink: 0 }}
            onClose={() => setCsvDownloadError(null)}
          >
            {csvDownloadError}
          </Alert>
        )}

        {!isCounter && report.description && (
          <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {report.description}
            </Typography>
          </Box>
        )}

        <CardContent
          {...(isStorageQuotaBar ? panelClickHandlers : {})}
          sx={{
            flex: isCounter ? '0 0 auto' : 1,
            display: 'flex',
            flexDirection: 'column',
            pt: isCounter ? 1.25 : 1.5,
            pb: isCounter ? 0 : 2,
            px: 2,
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
            ...(isStorageQuotaBar && isPanelClickable && {
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
              },
            }),
          }}
        >
          {renderChart()}
        </CardContent>

        {isCounter && report.description && (
          <Box sx={{ px: 2, pt: 0, pb: 1.5, mt: `${COUNTER_DESCRIPTION_GAP_PX}px` }}>
            <Typography variant="body2" color="text.secondary">
              {report.description}
            </Typography>
          </Box>
        )}
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <CardHeader
        title={
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              {vis.title || report.name}
            </Typography>
            <Chip size="small" label={vis.type} variant="outlined" />
            {contextBadge && (
              <Chip size="small" icon={<FilterAlt fontSize="small" />} label={contextBadge} color="secondary" />
            )}
            {isPanelClickable && (
              <Chip size="small" label="Click to drill down" variant="outlined" color="secondary" />
            )}
          </Stack>
        }
        subheader={
          <Typography variant="caption" color="text.secondary">
            {report.description || (report.query.timeRange?.preset ?? 'Custom range')}
          </Typography>
        }
        action={(
          <Stack direction="row" spacing={0.5} alignItems="center">
            {folderStorageDownloadAction}
            {uniqCounterDownloadAction}
            {apiAction}
            {editAction}
            {refreshAction}
          </Stack>
        )}
        sx={{ pb: 0 }}
      />
      <CardContent
        {...panelClickHandlers}
        sx={{
          flex: 1,
          pt: 1,
          minWidth: 0,
          overflow: 'hidden',
          ...(isPanelClickable && {
            cursor: 'pointer',
            '&:hover': { backgroundColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)') },
          }),
        }}
      >
        {renderChart()}
      </CardContent>
    </Card>
  );
};
